import React, { createContext, useContext, useEffect, useState } from 'react';

import { useUpdateLastSeenAtMutation } from '@/graphql';
import { isNil } from 'ramda';

export interface ActiveStatusContext {
	lastSeenAt: Date;
}

const defaultValue: ActiveStatusContext = {
	lastSeenAt: new Date(0),
};

const ActiveStatusContext = createContext<ActiveStatusContext>(defaultValue);

export function useActiveStatus(): ActiveStatusContext {
	const context = useContext(ActiveStatusContext);
	if (isNil(context)) {
		throw new Error(
			`useActiveStatus must be used within an ActiveStatusProvider`,
		);
	}

	return context;
}

export const ActiveStatusProvider: React.FC = ({ children }) => {
	const { data, mutateAsync: updateLastSeenAt } =
		useUpdateLastSeenAtMutation();

	const [lastSeenAt, setLastSeenAt] = useState(defaultValue.lastSeenAt);

	useEffect(() => {
		if (data?.updateLastSeenAt) {
			try {
				const date = new Date(data.updateLastSeenAt.lastSeenAt);
				console.log(
					`[ActiveStatusProvider] Received data after updating user's lastSeenAt`,
					date,
				);
				setLastSeenAt(date);
			} catch (err) {
				console.error(
					`[ActiveStatusProvider] Could not set user's lastSeenAt field`,
				);
			}
		}
	}, [data]);

	useEffect(() => {
		updateLastSeenAt({});

		const interval = setInterval(() => {
			console.log(
				`[ActiveStatusProvider] Sending update to the user's lastSeenAt`,
			);
			updateLastSeenAt({});
		}, 60 * 1000);
		return () => {
			clearInterval(interval);
		};
	}, [updateLastSeenAt]);

	return (
		<ActiveStatusContext.Provider
			value={{
				lastSeenAt,
			}}
		>
			{children}
		</ActiveStatusContext.Provider>
	);
};

export default ActiveStatusProvider;
